/**
 * Functional component that deals with multiple and single
 * availability exceptions, that we call timeslots.
 *
 * As Sharetribe Flex API doesn't support adding single timeslots
 * and rather use the availabilityPlan, we had to use exceptions
 * to manage timeslots.
 */
import React from 'react';
import { arrayOf, string, bool, func, object, oneOfType, array } from 'prop-types';
import { propTypes } from '../../util/types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import {
  DATE_TYPE_DATETIME,
  AVAILABILITY_EXCEPTION_METADATA_ACCESS_MEMBERS,
  AVAILABILITY_EXCEPTION_METADATA_ACCESS_TIER,
} from '../../util/types';
import { useConfirmationModal } from '../../util/hooks';
import { TimeRange, IconSpinner, InlineTextButton, Icon } from '..';
import classNames from 'classnames';
import moment from 'moment';

import css from './EditLiveRoomSchedulesPanel.module.css';

const formatRepeatingExceptionDates = exceptions => {
  const mappedEx = exceptions.map(e => {
    return moment(e.attributes.start).format('MMM DD');
  });
  const joinedEx = mappedEx.slice(0, -1).join(', ') + ' and ' + mappedEx.slice(-1);

  return exceptions.length > 1 ? joinedEx : mappedEx;
};

/**
 * Single availability exceptions
 */
const AvailabilityException = props => {
  const {
    timeZone,
    availabilityException,
    repeatingAvailabilityExceptions,
    availabilityExceptionMetadata,
    membershipTiers,
    deleteExceptionInProgress,
    deleteExceptionId,
    onDeleteAvailabilityException,
    onRescheduleAvailabilityException,
  } = props;

  const { start, end, seats } = availabilityException.attributes;
  const isUpcoming = new Date(start).getTime() > Date.now();
  const { title, access, membershipTierIds, membershipTierId } =
    availabilityExceptionMetadata?.attributes || {};

  // Find the membership tier names for members-only timeslots
  // Supports both new membershipTierIds (array) and legacy membershipTierId (single)
  const selectedTierIds = membershipTierIds || (membershipTierId ? [membershipTierId] : []);
  const membershipTierNames = selectedTierIds
    .map(id => membershipTiers?.find(m => m.id === id)?.name)
    .filter(Boolean);

  const isMainException = availabilityExceptionMetadata.type === 'exception';

  const repeat = isMainException && repeatingAvailabilityExceptions?.length > 0;
  const repeatingExceptionDates = formatRepeatingExceptionDates(repeatingAvailabilityExceptions);

  const showDeleteSpinner =
    deleteExceptionInProgress && deleteExceptionId?.uuid === availabilityException.id.uuid;

  const classes = classNames(css.exception, {
    [css.exceptionSubscription]:
      access === AVAILABILITY_EXCEPTION_METADATA_ACCESS_MEMBERS ||
      access === AVAILABILITY_EXCEPTION_METADATA_ACCESS_TIER,
  });
  const exceptionAvailabilityDotClasses = classNames(css.exceptionAvailabilityDot, {
    [css.isAvailable]: seats > 0,
  });

  return (
    <div className={classes}>
      <div className={css.exceptionHeader}>
        <div className={css.exceptionAvailability}>
          <div className={exceptionAvailabilityDotClasses} />
          <div className={css.exceptionAvailabilityStatus}>
            {title ? (
              title
            ) : seats > 0 ? (
              <FormattedMessage id="EditLiveRoomSchedulesPanel.exceptionAvailable" />
            ) : (
              <FormattedMessage id="EditLiveRoomSchedulesPanel.exceptionNotAvailable" />
            )}
          </div>
        </div>
        {isUpcoming && onRescheduleAvailabilityException ? (
          <InlineTextButton
            type="button"
            className={css.rescheduleExceptionButton}
            onClick={() =>
              onRescheduleAvailabilityException(
                availabilityException,
                availabilityExceptionMetadata
              )
            }
          >
            <FormattedMessage id="EditLiveRoomSchedulesPanel.reschedule" />
          </InlineTextButton>
        ) : null}
        <button
          type="button"
          className={css.deleteExceptionButton}
          onClick={() => onDeleteAvailabilityException({ id: availabilityException.id })}
        >
          {showDeleteSpinner ? (
            <IconSpinner className={css.deleteSpinnerIcon} />
          ) : (
            <Icon name="delete" size={16} className={css.deleteIcon} />
          )}
        </button>
      </div>
      <div className={css.exceptionFooter}>
        <div className={css.timeRangeContainer}>
          <TimeRange
            className={css.timeRange}
            startDate={start}
            endDate={end}
            dateType={DATE_TYPE_DATETIME}
            timeZone={availabilityExceptionMetadata?.attributes?.timeZone || timeZone}
          />
          {membershipTierNames.length > 0 && (
            <span className={css.membershipTierName}>· {membershipTierNames.join(', ')}</span>
          )}
        </div>
        {repeat ? (
          <span className={css.repeat}>
            <Icon name="repeat" size={12} className={css.repeatIcon} />
            <FormattedMessage
              id="EditLiveRoomSchedulesPanel.repeat"
              values={{ dates: repeatingExceptionDates }}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
};

AvailabilityException.defaultProps = {
  timeZone: null,
  availabilityExceptions: [],
  repeatingAvailabilityExceptions: [],
  availabilityExceptionMetadata: {},
  membershipTiers: [],
  fetchExceptionsInProgress: bool.isRequired,
  deleteExceptionId: null,
  onDeleteAvailabilityException: null,
};

AvailabilityException.propTypes = {
  timeZone: string.isRequired,
  availabilityException: propTypes.availabilityException,
  repeatingAvailabilityExceptions: arrayOf(propTypes.availabilityException).isRequired,
  availabilityExceptionMetadata: propTypes.availabilityExceptionMetadata,
  membershipTiers: array,
  deleteExceptionInProgress: bool.isRequired,
  deleteExceptionId: oneOfType([string, object]),
  onDeleteAvailabilityException: func.isRequired,
};

/**
 * Availability exceptions
 */
const AvailabilityExceptions = props => {
  const {
    intl,
    timeZone,
    availabilityExceptions,
    publicDataAvailabilityExceptions,
    membershipTiers,
    fetchExceptionsInProgress,
    deleteExceptionInProgress,
    deleteExceptionId,
    onDeleteAvailabilityException,
    onRescheduleAvailabilityException,
  } = props;

  const { open: onOpenConfirmationModal, modalElement } = useConfirmationModal();

  const exceptionCount = availabilityExceptions ? availabilityExceptions.length : 0;

  if (fetchExceptionsInProgress) {
    return (
      <div className={css.noExceptions}>
        <IconSpinner className={css.spinner} />
      </div>
    );
  } else if (exceptionCount === 0) {
    return (
      <div className={css.noExceptions}>
        <h3 className={css.emptyStateTitle}>
          <FormattedMessage id="EditLiveRoomSchedulesPanel.emptyStateTitle" />
        </h3>
        <p className={css.emptyStateText}>
          <FormattedMessage id="EditLiveRoomSchedulesPanel.emptyStateText" />
        </p>
        <p className={css.emptyStateTip}>
          <FormattedMessage id="EditLiveRoomSchedulesPanel.emptyStateTip" />
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={css.exceptions}>
        {availabilityExceptions.map(availabilityException => {
          const availabilityExceptionMetadata = publicDataAvailabilityExceptions.find(
            e => e.id === availabilityException.id.uuid
          );

          const repeatingAvailabilityExceptionIds = publicDataAvailabilityExceptions
            .filter(e => e.attributes.mainExceptionId === availabilityException.id.uuid)
            .map(e => e.id);
          const repeatingAvailabilityExceptions = availabilityExceptions.filter(e =>
            repeatingAvailabilityExceptionIds.includes(e.id.uuid)
          );
          return (
            <AvailabilityException
              key={availabilityException.id.uuid}
              timeZone={timeZone}
              availabilityException={availabilityException}
              repeatingAvailabilityExceptions={repeatingAvailabilityExceptions}
              availabilityExceptionMetadata={availabilityExceptionMetadata}
              membershipTiers={membershipTiers}
              deleteExceptionInProgress={deleteExceptionInProgress}
              deleteExceptionId={deleteExceptionId}
              onRescheduleAvailabilityException={onRescheduleAvailabilityException}
              onDeleteAvailabilityException={async params => {
                const confirm = await onOpenConfirmationModal({
                  id: 'EditLiveRoomSchedulesPanel.confirmDeleteException',
                  title: intl.formatMessage({
                    id: 'EditLiveRoomSchedulesPanel.confirmDeleteExceptionTitle',
                  }),
                  description: intl.formatMessage({
                    id: 'EditLiveRoomSchedulesPanel.confirmDeleteExceptionDescription',
                  }),
                });

                if (confirm) {
                  onDeleteAvailabilityException(params);
                }
              }}
            />
          );
        })}
      </div>
      {modalElement}
    </>
  );
};

AvailabilityExceptions.defaultProps = {
  timeZone: null,
  availabilityExceptions: [],
  publicDataAvailabilityExceptions: [],
  fetchExceptionsInProgress: false,
  deleteExceptionInProgress: false,
  deleteExceptionId: null,
  onDeleteAvailabilityException: null,
};

AvailabilityExceptions.propTypes = {
  timeZone: string.isRequired,
  availabilityExceptions: arrayOf(propTypes.availabilityException).isRequired,
  publicDataAvailabilityExceptions: arrayOf(propTypes.availabilityExceptionMetadata).isRequired,
  fetchExceptionsInProgress: bool.isRequired,
  deleteExceptionInProgress: bool.isRequired,
  deleteExceptionId: string,
  onDeleteAvailabilityException: func.isRequired,
};

export default AvailabilityExceptions;
